/**
 * Drawing Drag & Displacement Engine.
 *
 * Displaces geometric points according to pixel deltas using screen-to-chart
 * coordinate extrapolation and updates multi-point and horizontal line drawings.
 */

import { normalizePosition } from "./position_tool";

export interface ChartPoint {
  time: number;
  price: number;
}

export interface DrawingRecord {
  id?: string;
  type?: string;
  p1?: ChartPoint;
  p2?: ChartPoint;
  p3?: ChartPoint;
  point1?: ChartPoint;
  point2?: ChartPoint;
  price?: number;
  [key: string]: any;
}

export interface DragState {
  id: string;
  mode: string; // 'move' | 'resize-p1' | 'resize-p2' | 'resize-p3' | string
  startX?: number;
  startY?: number;
  origSnapshot: Record<string, any>;
}

/**
 * Displaces a single chart coordinate point by pixel offsets.
 */
export function displaceChartPoint(
  origPt: ChartPoint | null | undefined,
  dxPx: number,
  dyPx: number,
  coordToPixel: (time: number, price: number) => { x: number; y: number } | null,
  pixelToTime: (x: number) => number | null,
  pixelToPrice: (y: number) => number | null,
): ChartPoint | undefined {
  if (!origPt) return undefined;
  const cur = coordToPixel(origPt.time, origPt.price);
  if (!cur) return origPt;

  const newX = cur.x + dxPx;
  const newY = cur.y + dyPx;
  const newTime = pixelToTime(newX);
  const newPrice = pixelToPrice(newY);

  return {
    time: newTime !== null ? newTime : origPt.time,
    price: newPrice !== null ? newPrice : origPt.price,
  };
}

/**
 * Applies drag displacement delta across drawing points based on drag mode.
 */
export function applyDrawingDragDelta(
  d: DrawingRecord | null | undefined,
  drag: DragState | null | undefined,
  dxPx: number,
  dyPx: number,
  coordToPixel: (time: number, price: number) => { x: number; y: number } | null,
  pixelToTime: (x: number) => number | null,
  pixelToPrice: (y: number) => number | null,
  minTimeGap?: number,
): void {
  if (!d || !drag || !drag.origSnapshot) return;

  // Anchored VWAP: no points, only `anchorTime`. Dragging the line ('move') or the anchor handle ('resize-p1') moves the
  // ANCHOR horizontally; the price follows from the candles, so the vertical delta is ignored. Computed from the SNAPSHOT
  // so repeated mouse-move deltas do not accumulate. The page clamps the result onto the data (anchored_vwap.ts).
  if (d.type === "avwap") {
    if (drag.mode === "move" || drag.mode === "resize-p1") {
      const orig = drag.origSnapshot.anchorTime;
      if (typeof orig === "number") {
        const cur = coordToPixel(orig, 0);
        if (cur) {
          const t = pixelToTime(cur.x + dxPx);
          if (t !== null) d.anchorTime = t;
        }
      }
    }
    return;
  }

  const movePt = (origPt: ChartPoint | undefined) =>
    displaceChartPoint(origPt, dxPx, dyPx, coordToPixel, pixelToTime, pixelToPrice);

  if (drag.mode === "move") {
    if (d.p1) d.p1 = movePt(drag.origSnapshot.p1);
    if (d.point1) d.point1 = movePt(drag.origSnapshot.p1);
    if (d.p2) d.p2 = movePt(drag.origSnapshot.p2);
    if (d.point2) d.point2 = movePt(drag.origSnapshot.p2);
    if (d.p3) d.p3 = movePt(drag.origSnapshot.p3);
    if (d.price !== undefined && drag.origSnapshot.price !== undefined) {
      const cur = coordToPixel(0, drag.origSnapshot.price);
      if (cur) {
        const np = pixelToPrice(cur.y + dyPx);
        if (np !== null) d.price = np;
      }
    }
  } else if (drag.mode === "resize-p1") {
    if (d.p1) d.p1 = movePt(drag.origSnapshot.p1);
    if (d.point1) d.point1 = movePt(drag.origSnapshot.p1);
  } else if (drag.mode === "resize-p2") {
    if (d.p2) d.p2 = movePt(drag.origSnapshot.p2);
    if (d.point2) d.point2 = movePt(drag.origSnapshot.p2);
  } else if (drag.mode === "resize-p3") {
    if (d.p3) d.p3 = movePt(drag.origSnapshot.p3);
  }

  // The engine displaces every point independently. A Long/Short Position has
  // invariants across its points (p2/p3 share a right edge; stop < entry < target),
  // so restore them here — the page never has to know. `minTimeGap` is the
  // minimum box width (the page passes one candle in seconds).
  if (d.type === "rr_position") {
    normalizePosition(d, drag.mode, minTimeGap !== undefined ? { minTimeGap } : {});
  }
}
